import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/sections/Footer";
import StickyMobileCTA from "../components/StickyMobileCTA";
import Seo from "../components/Seo";
import { Reveal, Overline, Starburst } from "../components/common";
import { useLead } from "../components/LeadDialog";
import { getServicePage, SERVICE_PAGES } from "../data/servicePages";

const SITE = "https://jetcleaningusa.com";

const ServicePage = () => {
  const { slug } = useParams();
  const { openLead } = useLead();
  const page = getServicePage(slug);

  if (!page) return <Navigate to="/" replace />;

  const related = (page.related || [])
    .map((s) => getServicePage(s))
    .filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: page.h1,
        serviceType: page.h1,
        description: page.description,
        provider: { "@type": "CleaningService", name: "Jet Cleaning Service" },
        areaServed: [
          { "@type": "City", name: "Denton", addressRegion: "TX" },
          { "@type": "AdministrativeArea", name: "Dallas–Fort Worth Metroplex" },
        ],
        url: `${SITE}/${page.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          {
            "@type": "ListItem",
            position: 2,
            name: page.h1,
            item: `${SITE}/${page.slug}`,
          },
        ],
      },
    ],
  };

  return (
    <div className="bg-background">
      <Seo
        title={page.title}
        description={page.description}
        path={`/${page.slug}`}
        jsonLd={jsonLd}
      />
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden" data-testid="service-hero">
          <div className="max-w-[88rem] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-7">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-1.5 text-sm font-semibold text-jet-ink/50 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-jet-teal">Home</Link>
                <ChevronRight className="h-4 w-4" />
                <span className="text-jet-ink/70">{page.h1}</span>
              </nav>

              <Overline className="mb-5">{page.tag}</Overline>
              <h1 className="font-display font-semibold text-jet-ink text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
                {page.h1}
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg text-jet-ink/70 leading-relaxed">
                {page.intro}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => openLead("quote")}
                  className="rounded-full bg-jet-teal hover:bg-jet-teal-deep text-white font-bold h-14 px-8 text-base teal-glow group"
                  data-testid="service-quote-button"
                >
                  Request a Quote
                  <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <div className="relative overflow-hidden arch-mask shadow-2xl aspect-[3/4]">
                  <img
                    src={page.image}
                    alt={`${page.h1} — Jet Cleaning Service`}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                <Starburst className="absolute -left-5 top-10 h-12 w-12 text-jet-amber" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Included */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-[88rem] mx-auto px-5 md:px-10">
            <Reveal>
              <Overline className="mb-4">What's Included</Overline>
              <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-jet-ink max-w-3xl">
                A thorough clean, done right.
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {page.included.map((item, i) => (
                <Reveal key={item} delay={(i % 4) * 0.05}>
                  <div className="h-full flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
                    <span className="grid place-items-center h-6 w-6 rounded-full bg-jet-teal/15 text-jet-teal shrink-0 mt-0.5">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    <span className="font-semibold text-jet-ink/80">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Why */}
        <section className="py-20 md:py-24 bg-background">
          <div className="max-w-[88rem] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Reveal>
                <Overline className="mb-4">Why Jet Cleaning</Overline>
                <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-jet-ink">
                  Dependable. Detailed. Stress-free.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {page.why.map((w, i) => (
                <Reveal key={w} delay={i * 0.06}>
                  <div className="h-full rounded-2xl border border-border bg-card p-6 flex items-start gap-3">
                    <Starburst className="h-5 w-5 text-jet-amber shrink-0 mt-0.5" />
                    <span className="font-medium text-jet-ink/80">{w}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 bg-background">
          <div className="max-w-[88rem] mx-auto px-5 md:px-10">
            <Reveal>
              <div className="relative overflow-hidden rounded-[2rem] bg-jet-teal text-white px-6 py-14 md:px-16 md:py-20 text-center">
                <Starburst className="absolute -left-10 -top-10 h-48 w-48 text-white/10" />
                <div className="relative">
                  <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight max-w-3xl mx-auto">
                    Ready for a spotless result?
                  </h2>
                  <p className="mt-4 text-white/80 max-w-xl mx-auto">
                    Serving the entire Dallas–Fort Worth Metroplex, including
                    Denton, TX — with fast, honest pricing.
                  </p>
                  <Button
                    onClick={() => openLead("quote")}
                    className="mt-8 rounded-full bg-jet-amber hover:bg-jet-amber/90 text-white font-bold h-14 px-8 text-base shadow-lg"
                    data-testid="service-cta-bottom"
                  >
                    Request a Quote
                  </Button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="py-20 md:py-24 bg-white">
            <div className="max-w-[88rem] mx-auto px-5 md:px-10">
              <Reveal>
                <Overline className="mb-4">Related Services</Overline>
                <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-jet-ink">
                  Explore more of what we do.
                </h2>
              </Reveal>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((r, i) => (
                  <Reveal key={r.slug} delay={i * 0.08}>
                    <Link
                      to={`/${r.slug}`}
                      className="group block h-full rounded-2xl border border-border bg-card p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
                      data-testid={`related-${r.slug}`}
                    >
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-jet-teal-deep mb-2">
                        {r.tag}
                      </div>
                      <h3 className="font-display text-xl font-semibold text-jet-ink flex items-center justify-between gap-2">
                        {r.short}
                        <ArrowUpRight className="h-5 w-5 text-jet-teal transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </h3>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <StickyMobileCTA />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
};

export default ServicePage;
